import React, { useRef, useState, useEffect, useCallback } from 'react';
import MaterialTable from 'material-table';
import { useTranslation } from 'react-i18next';
import { Link } from '@material-ui/core';
import { getEvaluationsListReq, EvaluationCtx } from '../../api/evaluation/listApi';
import { EvaluationModel } from '../../models/evaluationModel';
import { useHistory } from 'react-router-dom';
import { RemoteResourceModel } from '../../models/remoteResourceModel';
import moment from 'moment';
import { deleteEvaluationReq } from '../../api/evaluation/editApi';
import { personHasRole } from '../../utils/personUtils';
import { PersonRole } from '../../models/eums/personRole';
import { localization } from 'config/localization';

interface Props {
  forPerson?: string;
  showToolbar?: boolean;
}

export function EvalueeTable(props: Props) {
  const { t } = useTranslation();
  const dataTableRef = useRef<MaterialTable<EvaluationModel>>(null);
  const [evaluations, setEvaluations] = useState<EvaluationModel[]>([]);
  const history = useHistory();
  const { forPerson, showToolbar } = props;
  const isPersonAdmin = personHasRole(PersonRole.Admin);

  async function getEvaluations(personId?: string) {
    let evaluationResp;
    if (personId) evaluationResp = await getEvaluationsListReq(EvaluationCtx.SUBJECT, personId);
    else evaluationResp = await getEvaluationsListReq(EvaluationCtx.SUBJECT);

    if (evaluationResp && evaluationResp.data) setEvaluations(evaluationResp.data);
  }

  const getEvaluationsMemo = useCallback(() => getEvaluations(forPerson), [forPerson]);

  useEffect(() => {
    getEvaluationsMemo();
  }, [getEvaluationsMemo]);

  return (
    <MaterialTable<EvaluationModel>
      options={{
        pageSize: 10,
        showTitle: true,
        toolbar: typeof showToolbar === 'boolean' ? showToolbar : true,
        search: true,
        headerStyle: {
          fontWeight: 'bold',
          backgroundColor: '#F5F5F5',
        },
      }}
      title={t('evaluations_by_others')}
      tableRef={dataTableRef}
      localization={localization()}
      actions={
        isPersonAdmin
          ? [
              {
                icon: 'delete',
                tooltip: t('delete'),
                onClick: async (event, rowData) => {
                  const rd = rowData as EvaluationModel;
                  if (!rd._id) return;

                  if (window.confirm(`${t('are_you_sure_delete')} ${t('evaluation')}?`)) {
                    await deleteEvaluationReq(rd._id);
                    await getEvaluations(forPerson);
                  }
                },
              },
            ]
          : undefined
      }
      columns={[
        {
          title: t('evaluator'),
          sorting: true,
          customSort: (a, b) => {
            const aStr = `${a.evaluator?.preferredGivenName || ''}${
              a.evaluator?.preferredFamilyName || ''
            }`
              .toLowerCase()
              .trim();
            const bStr = `${b.evaluator?.preferredGivenName || ''}${
              b.evaluator?.preferredFamilyName || ''
            }`
              .toLowerCase()
              .trim();
            return aStr > bStr ? 1 : aStr === bStr ? 0 : -1;
          },
          customFilterAndSearch: (filterVal: string, rowData) => {
            const fullName = `${rowData.evaluator?.preferredGivenName || ''}${
              rowData.evaluator?.preferredFamilyName || ''
            }`
              .toLowerCase()
              .trim();

            if (fullName.indexOf(filterVal.toLowerCase().trim()) > 0) return true;

            return false;
          },
          render: val =>
            `${val.evaluator?.preferredGivenName} ${val.evaluator?.preferredFamilyName}`,
        },
        {
          title: t('tournament'),
          field: 'tournament.name',
          sorting: true,
          render: val => (
            <Link
              onClick={() =>
                history.push(`/tournament-calendar/tournament/${val.tournament?.tournamentId}`)
              }
            >
              {`${val.tournament?.name || ''}`}
            </Link>
          ),
        },
        {
          title: t('tournament_start'),
          field: 'tournament.start',
          sorting: true,
          render: val =>
            val.tournament?.start && moment(val.tournament.start).format('DD.MM.YYYY.'),
        },
        {
          title: t('tournament_end'),
          field: 'tournament.end',
          sorting: true,
          render: val => val.tournament?.end && moment(val.tournament.end).format('DD.MM.YYYY.'),
        },
        {
          title: t('rating'),
          field: 'rating',
          sorting: true,
        },
        {
          title: t('created_at'),
          field: 'createdAt',
          sorting: true,
          defaultSort: 'desc',
          render: val => moment(val.createdAt).format('DD.MM.YYYY.'),
        },
        {
          title: t('evaluation'),
          field: 'file',
          render: val => {
            const vFile = val.file as RemoteResourceModel;
            if (!vFile) return <></>;

            return <Link href={vFile.url}>{vFile.fileName}</Link>;
          },
        },
      ]}
      data={evaluations}
    />
  );
}
